import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { queryClient } from '@/lib/api-client.ts'
import { getCurrentUser, userQueryKey } from '@/lib/actions.ts'
import { editUser } from '@/routes/settings/account/actions.ts'
import { redirectToLastActivePath } from '@/lib/helpers/redirectToLastActivePath.ts'
import { switchOrganization } from '@/lib/helpers/switchOrganization.ts'
import { closeAllMercureConnections } from '@/lib/helpers/subscribeToMercureTopic'
import { OrganizationMembership, useUserStore } from '@/store/user.ts'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useLocallyStoredFilters } from '@/routes/projectStages/issues/lib/stores/useLocallyStoredFilters.ts'
import { usePrefetchStore } from '@/store/usePrefetchStore.ts'
import { toast } from '@/lib/hooks/useToast.ts'

type MembershipWithIri = OrganizationMembership & { '@id'?: string }

/** Where to land when the new organization yields no usable stage to navigate to. */
const SAFE_ROUTE = '/settings/account'

/**
 * Lets an ordinary (non-superuser) member move between the organizations they belong to.
 *
 * The superuser console has its own richer flow (`useActivateOrganization`) which may also
 * CREATE a membership on the fly; this one only ever activates a membership the user already
 * holds, which is all the server permits for a self-edit (`UserStateProcessor` rejects any
 * membership not in the target user's own collection).
 */
export function useSwitchOrganization() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const user = useUserStore(state => state.user)
  const setUser = useUserStore(state => state.setUser)
  const setCurrentOrganizationMembership = useUserStore(
    state => state.setCurrentOrganizationMembership,
  )
  const setCurrentOrganization = useUserStore(state => state.setCurrentOrganization)
  const resetStageStore = useProjectStageStore(state => state.resetStageStore)
  const requestForceRefetch = usePrefetchStore(state => state.requestForceRefetch)
  const tabs = useLocallyStoredFilters(s => s.tabs)

  // A ref, not the state flag: two clicks landing in the same React batch would both read
  // `isSwitching === false` and fire overlapping switches whose responses could resolve out of
  // order, leaving the store on one organization while the server is on another.
  const switchingRef = useRef(false)
  const [isSwitching, setIsSwitching] = useState(false)

  const switchTo = async (membershipIri: string): Promise<void> => {
    if (switchingRef.current) return

    if (!navigator.onLine) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('navigation.organizationSwitchOffline'),
      })
      return
    }

    switchingRef.current = true
    setIsSwitching(true)
    try {
      const result = await switchOrganization({
        user,
        membershipIri,
        queryClient,
        patchActiveMembership: (userId, iri) => editUser(userId, { activeMembership: iri }),
        fetchCurrentUser: getCurrentUser,
        closeRealtimeConnections: closeAllMercureConnections,
        resetStageStore,
        requestOfflinePrefetch: () => requestForceRefetch(),
        applyUser: nextUser => {
          queryClient.setQueryData(userQueryKey, nextUser)
          setUser(nextUser)

          const membership = nextUser.organizationMemberships?.find(
            candidate =>
              (candidate as MembershipWithIri | undefined)?.['@id'] === nextUser.activeMembership,
          ) as OrganizationMembership | undefined

          if (membership) {
            setCurrentOrganizationMembership(membership)
            if (membership.organization) setCurrentOrganization(membership.organization)
          }
        },
        redirect: async nextUser => {
          // redirectToLastActivePath resolves without navigating when it can neither fetch nor
          // find cached participations — and we just purged that cache. Landing nowhere would
          // strand the user on a URL belonging to the organization they just left.
          let navigated = false
          await redirectToLastActivePath({
            user: nextUser,
            tabs,
            navigate: path => {
              navigated = true
              navigate(path)
            },
          })
          if (!navigated) navigate(SAFE_ROUTE)
        },
      })

      if (result.status === 'failed') {
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: t('navigation.organizationSwitchFailed'),
        })
        // The server pointer already moved, so the app must not stay on the old organization's
        // screen just because a follow-up request failed.
        if (result.committed) navigate(SAFE_ROUTE)
      }
    } finally {
      switchingRef.current = false
      setIsSwitching(false)
    }
  }

  return { switchTo, isSwitching }
}
